import React, { useState, useEffect, useCallback, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, { Draggable } from '@fullcalendar/interaction';
import axios from 'axios';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { X, User, Trash2 } from 'react-feather';
import PageHeader from '../../shared/page-header/PageHeader';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { logAuditEvent } from '../../utils/auditLogger';
import SKAlerts, { CreateAlert } from '../../shared/common-alerts/SKAlerts';
import { getStorageValues } from '../../shared/CommonFunctions';
import TodoPanel from './TodoPanel';
import EventPanel from './EventPanel';
import './Calendar.css';

const CalendarMain = ({ Module = 'Calendar' }) => {
    const [calendarEvents, setCalendarEvents] = useState([]);
    const [todoItems, setTodoItems] = useState([]);
    const [isLoadingTodos, setIsLoadingTodos] = useState(false);
    const [alert, setAlert] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showEventModal, setShowEventModal] = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [isSavingCalendarEvent, setIsSavingCalendarEvent] = useState(false);
    const [scheduleDraft, setScheduleDraft] = useState({
        eventTypeId: null,
        eventTypeName: ''
    });
    const [scheduleForm, setScheduleForm] = useState({
        title: '',
        description: '',
        startDateTime: null,
        endDateTime: null
    });
    const todoPanelRef = useRef(null);
    const eventPanelRef = useRef(null);
    const userId = getStorageValues('userId');
    const isAdminUser = getStorageValues('IsAdmin') === 'true';

    // Fetch calendar events
    const fetchCalendarEvents = useCallback(async ({ preserveExistingOnEmpty = false } = {}) => {
        try {
            const userIdNumber = Number(userId);
            if (!userIdNumber) {
                CreateAlert('User session not found. Please log in again.', 'Error', setAlert);
                return;
            }

            const response = await axios.post(
                getApiBaseUrl() + '/calendar/getCalendarEventsV2',
                { UserId: userIdNumber }
            );

            if (response.data?.success) {
                // Transform database events to FullCalendar format
                const calendarRows = Array.isArray(response.data?.data?.[0])
                    ? response.data.data[0]
                    : (Array.isArray(response.data?.data) ? response.data.data : []);

                const visibleRows = isAdminUser
                    ? calendarRows
                    : calendarRows.filter((event) => event?.EventType !== 'Birthday');

                const transformedEvents = visibleRows.map(event => ({
                    id: `${event.EventType}-${event.EventId}`,
                    title: event.EventTitle,
                    start: event.EventDate,
                    end: event.EndDate || null,
                    backgroundColor: event.EventColor,
                    borderColor: event.EventColor,
                    editable: false,
                    extendedProps: {
                        eventType: event.EventType,
                        clientName: event.ClientName,
                        clientId: event.ClientId,
                        description: event.EventDescription,
                        assignedTo: event.AssignedTo,
                        taskKey: event.TaskKey,
                        rawEventId: Number(event.EventId),
                        relatedId: event.RelatedId,
                        oldDate: event.EventDate,
                        endDate: event.EndDate || null
                    }
                }));

                setCalendarEvents((prevEvents) => {
                    if (preserveExistingOnEmpty && transformedEvents.length === 0 && prevEvents.length > 0) {
                        return prevEvents;
                    }
                    return transformedEvents;
                });
            }
        } catch (error) {
            CreateAlert('Failed to fetch calendar events', 'Error', setAlert);
        }
    }, [isAdminUser, userId]);

    const fetchTodoItems = useCallback(async () => {
        try {
            const userIdNumber = Number(userId);
            if (!userIdNumber) {
                return;
            }

            setIsLoadingTodos(true);
            const response = await axios.get(getApiBaseUrl() + '/todolist/getAllV2', {
                params: {
                    UserId: userIdNumber,
                    IsScheduled: 0
                }
            });

            if (response.data?.success) {
                const todoRows = Array.isArray(response.data?.data?.[0])
                    ? response.data.data[0]
                    : (Array.isArray(response.data?.data) ? response.data.data : []);
                setTodoItems(todoRows);
            } else {
                setTodoItems([]);
            }
        } catch (error) {
            setTodoItems([]);
            CreateAlert('Failed to fetch todo list items', 'Error', setAlert);
        } finally {
            setIsLoadingTodos(false);
        }
    }, [userId]);

    // Handle event drag and drop
    const handleEventDrop = async (info) => {
        try {
            const event = info.event;
            const oldDate = event.extendedProps.oldDate;
            const newDate = event.start;
            const eventType = event.extendedProps.eventType;
            const eventId = Number(event.extendedProps.rawEventId || event.extendedProps.relatedId || 0);
            const clientId = event.extendedProps.clientId;
            const clientName = event.extendedProps.clientName;

            if (!eventId) {
                CreateAlert('Unable to identify event for update', 'Error', setAlert);
                info.revert();
                return;
            }

            const response = await axios.post(
                getApiBaseUrl() + '/calendar/updateCalendarEventDate',
                {
                    EventId: eventId,
                    EventType: eventType,
                    OldDate: oldDate,
                    NewDate: newDate,
                    UserId: Number(userId),
                    ClientId: clientId,
                    ClientName: clientName
                }
            );

            if (response.data?.success) {
                // Update the event in local state
                event.setExtendedProp('oldDate', newDate);
                CreateAlert(`Event updated to ${moment(newDate).format('YYYY-MM-DD')}`, 'Success', setAlert);

                // Log audit event
                logAuditEvent(Module, 'Event Drag-Drop', JSON.stringify({
                    EventId: eventId,
                    EventType: eventType,
                    OldDate: oldDate,
                    NewDate: newDate
                }));
            }
        } catch (error) {
            info.revert();
            CreateAlert('Failed to update event date', 'Error', setAlert);
        }
    };

    const handleExternalEventReceive = async (info) => {
        const sourceType = info.event.extendedProps?.sourceType;

        if (sourceType === 'calendarEvent') {
            const droppedDate = info.event.start || new Date();
            const defaultStart = moment(droppedDate).seconds(0).milliseconds(0);
            const defaultEnd = defaultStart.clone().add(1, 'hour');

            setScheduleDraft({
                eventTypeId: Number(info.event.extendedProps?.eventTypeId || 0),
                eventTypeName: info.event.extendedProps?.eventTypeName || info.event.title || 'Event'
            });

            setScheduleForm({
                title: info.event.extendedProps?.eventTypeName || info.event.title || 'Event',
                description: '',
                startDateTime: defaultStart.toDate(),
                endDateTime: defaultEnd.toDate()
            });

            info.event.remove();
            setShowScheduleModal(true);
            return;
        }

        const todoId = info.event.extendedProps?.todoId
            ? Number(info.event.extendedProps.todoId)
            : null;

        if (!todoId) {
            info.revert();
            CreateAlert('Invalid todo item selected for scheduling', 'Error', setAlert);
            return;
        }

        try {
            const scheduledDate = moment(info.event.start).format('YYYY-MM-DD HH:mm:ss');
            const payload = {
                TodoId: todoId,
                ScheduledDate: scheduledDate,
                UserId: Number(userId)
            };

            const response = await axios.post(
                getApiBaseUrl() + '/todolist/scheduleOnCalendar',
                payload
            );

            if (response.data?.success) {
                CreateAlert(response.data.message || 'Todo scheduled successfully', 'Success', setAlert);
                logAuditEvent(Module, 'Todo Drag-Drop', JSON.stringify({
                    TodoId: todoId,
                    ScheduledDate: scheduledDate
                }));
                await fetchCalendarEvents();
                await fetchTodoItems();
            } else {
                info.revert();
                CreateAlert(response.data?.message || 'Unable to schedule todo', 'Error', setAlert);
            }
        } catch (error) {
            info.revert();
            CreateAlert(error?.response?.data?.message || 'Failed to schedule todo', 'Error', setAlert);
        }
    };

    const handleScheduleInputChange = (event) => {
        const { name, value } = event.target;
        setScheduleForm((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleStartDateTimeChange = (date) => {
        setScheduleForm((prev) => ({
            ...prev,
            startDateTime: date,
            endDateTime: date ? moment(date).add(1, 'hour').toDate() : null
        }));
    };

    const handleScheduleModalClose = () => {
        setShowScheduleModal(false);
        setScheduleDraft({
            eventTypeId: null,
            eventTypeName: ''
        });
        setScheduleForm({
            title: '',
            description: '',
            startDateTime: null,
            endDateTime: null
        });
    };

    const handleSaveCalendarEvent = async () => {
        const title = scheduleForm.title?.trim();
        const startDateTime = scheduleForm.startDateTime;
        const endDateTime = scheduleForm.endDateTime;

        if (!title || !startDateTime || !endDateTime) {
            CreateAlert('Title, start datetime and end datetime are required', 'Error', setAlert);
            return;
        }

        if (moment(endDateTime).isBefore(moment(startDateTime))) {
            CreateAlert('End datetime cannot be before start datetime', 'Error', setAlert);
            return;
        }

        try {
            setIsSavingCalendarEvent(true);
            const startDateTimeSql = moment(startDateTime).format('YYYY-MM-DD HH:mm:ss');
            const endDateTimeSql = moment(endDateTime).format('YYYY-MM-DD HH:mm:ss');

            const response = await axios.post(getApiBaseUrl() + '/calendar/saveCalendarEvent', {
                EventTitle: title,
                EventDescription: scheduleForm.description?.trim() || null,
                StartDateTime: startDateTimeSql,
                EndDateTime: endDateTimeSql,
                UserId: Number(userId)
            });

            if (response.data?.success) {
                const createdEventId = Number(response.data?.data?.CalendarEventLogId || 0);
                const createdEventCompositeId = createdEventId
                    ? `CalendarEvent-${createdEventId}`
                    : `CalendarEvent-temp-${Date.now()}`;
                const optimisticEvent = {
                    id: createdEventCompositeId,
                    title,
                    start: startDateTimeSql,
                    end: endDateTimeSql,
                    backgroundColor: '#16a085',
                    borderColor: '#16a085',
                    editable: false,
                    extendedProps: {
                        eventType: 'CalendarEvent',
                        clientName: null,
                        clientId: null,
                        description: scheduleForm.description?.trim() || null,
                        assignedTo: null,
                        taskKey: null,
                        rawEventId: createdEventId || null,
                        relatedId: createdEventId || null,
                        oldDate: startDateTimeSql,
                        endDate: endDateTimeSql
                    }
                };

                setCalendarEvents((prevEvents) => {
                    const eventsWithoutDuplicate = createdEventCompositeId
                        ? prevEvents.filter((item) => String(item.id) !== String(createdEventCompositeId))
                        : prevEvents;
                    return [...eventsWithoutDuplicate, optimisticEvent];
                });

                CreateAlert(response.data?.message || 'Calendar event created successfully', 'Success', setAlert);
                logAuditEvent(Module, 'Calendar Event Create', JSON.stringify({
                    EventType: scheduleDraft.eventTypeName,
                    Title: title,
                    StartDateTime: startDateTime,
                    EndDateTime: endDateTime
                }));
                handleScheduleModalClose();
                void fetchCalendarEvents({ preserveExistingOnEmpty: true });
            } else {
                CreateAlert(response.data?.message || 'Unable to create calendar event', 'Error', setAlert);
            }
        } catch (error) {
            CreateAlert(error?.response?.data?.message || 'Failed to create calendar event', 'Error', setAlert);
        } finally {
            setIsSavingCalendarEvent(false);
        }
    };

    // Handle event click
    const handleEventClick = (info) => {
        const event = info.event;
        setSelectedEvent({
            id: event.id,
            title: event.title,
            start: event.start,
            end: event.end,
            eventType: event.extendedProps.eventType,
            clientName: event.extendedProps.clientName,
            description: event.extendedProps.description,
            assignedTo: event.extendedProps.assignedTo,
            taskKey: event.extendedProps.taskKey,
            relatedId: event.extendedProps.relatedId,
            rawEventId: event.extendedProps.rawEventId
        });
        setShowEventModal(true);

        // Log audit event
        logAuditEvent(Module, 'Event Click', JSON.stringify({
            EventId: event.id,
            EventTitle: event.title,
            EventType: event.extendedProps.eventType
        }));
    };

    // Handle modal close
    const handleModalClose = () => {
        setShowEventModal(false);
        setSelectedEvent(null);
    };

    const handleDeleteCalendarEvent = async () => {
        if (!selectedEvent?.id || selectedEvent.eventType !== 'CalendarEvent') {
            return;
        }

        const calendarEventLogId = Number(selectedEvent.relatedId || selectedEvent.rawEventId || 0);
        if (!calendarEventLogId) {
            CreateAlert('Unable to identify calendar event for delete', 'Error', setAlert);
            return;
        }

        try {
            const response = await axios.post(getApiBaseUrl() + '/calendar/deleteCalendarEvent', {
                CalendarEventLogId: calendarEventLogId,
                UserId: Number(userId)
            });

            if (response.data?.success) {
                CreateAlert(response.data?.message || 'Calendar event deleted successfully', 'Success', setAlert);
                logAuditEvent(Module, 'Calendar Event Delete', JSON.stringify({
                    CalendarEventLogId: calendarEventLogId,
                    EventTitle: selectedEvent.title
                }));
                handleModalClose();
                await fetchCalendarEvents();
            } else {
                CreateAlert(response.data?.message || 'Unable to delete calendar event', 'Error', setAlert);
            }
        } catch (error) {
            CreateAlert(error?.response?.data?.message || 'Failed to delete calendar event', 'Error', setAlert);
        }
    };

    // Log module access on mount
    useEffect(() => {
        logAuditEvent(Module, 'Accessed Module', `Accessed ${Module}`);
        fetchCalendarEvents();
        fetchTodoItems();
    }, [Module, fetchCalendarEvents, fetchTodoItems]);

    useEffect(() => {
        if (!todoPanelRef.current || !todoItems.length) {
            return undefined;
        }

        const draggable = new Draggable(todoPanelRef.current, {
            itemSelector: '.todo-item',
            eventData: (eventEl) => ({
                title: eventEl.getAttribute('data-title') || 'Todo',
                duration: '01:00',
                backgroundColor: '#e67e22',
                borderColor: '#e67e22',
                extendedProps: {
                    sourceType: 'todo',
                    eventType: 'Todo',
                    todoId: eventEl.getAttribute('data-todo-id'),
                    description: eventEl.getAttribute('data-description') || ''
                }
            })
        });

        return () => {
            draggable.destroy();
        };
    }, [todoItems]);

    useEffect(() => {
        if (!eventPanelRef.current) {
            return undefined;
        }

        const draggable = new Draggable(eventPanelRef.current, {
            itemSelector: '.event-item',
            eventData: (eventEl) => ({
                title: eventEl.getAttribute('data-event-name') || 'Event',
                duration: '01:00',
                backgroundColor: '#16a085',
                borderColor: '#16a085',
                extendedProps: {
                    sourceType: 'calendarEvent',
                    eventType: 'CalendarEvent',
                    eventTypeId: eventEl.getAttribute('data-event-type-id'),
                    eventTypeName: eventEl.getAttribute('data-event-name') || 'Event'
                }
            })
        });

        return () => {
            draggable.destroy();
        };
    }, []);

    return (
        <div className="calendar-container">
            <PageHeader pageTitle="Calendar" />
            <SKAlerts alert={alert} />

            <div className="calendar-layout">
                <div className="left-panel-column">
                    <TodoPanel
                        todos={todoItems}
                        isLoading={isLoadingTodos}
                        onRefresh={fetchTodoItems}
                        panelRef={todoPanelRef}
                    />
                    <EventPanel panelRef={eventPanelRef} />
                </div>

                <div className="calendar-wrapper">
                    <FullCalendar
                        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                        headerToolbar={{
                            left: 'prev,next today',
                            center: 'title',
                            right: 'dayGridMonth,timeGridWeek,timeGridDay'
                        }}
                        initialView="dayGridMonth"
                        events={calendarEvents}
                        editable={true}
                        droppable={true}
                        eventDisplay="block"
                        eventDrop={handleEventDrop}
                        eventReceive={handleExternalEventReceive}
                        eventClick={handleEventClick}
                        height="auto"
                    />
                </div>
            </div>

            {showEventModal && selectedEvent && (
                <div className="modal-overlay" onClick={handleModalClose}>
                    <div className="event-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h5 className="modal-title">{selectedEvent.title}</h5>
                            <button
                                className="btn-close"
                                onClick={handleModalClose}
                                aria-label="Close"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            {selectedEvent.clientName && (
                                <div className="event-detail-item">
                                    <label>Client Name</label>
                                    <div className="event-detail-value">
                                        <User size={16} style={{ marginRight: '8px' }} />
                                        {selectedEvent.clientName}
                                    </div>
                                </div>
                            )}

                            <div className="event-detail-item">
                                <label>Event Type</label>
                                <div className="event-detail-value">
                                    <span className={`badge ${selectedEvent.eventType === 'Birthday' ? 'badge-birthday' : selectedEvent.eventType === 'Todo' ? 'badge-todo' : selectedEvent.eventType === 'CalendarEvent' ? 'badge-calendar-event' : 'badge-followup'}`}>
                                        {selectedEvent.eventType || 'N/A'}
                                    </span>
                                </div>
                            </div>

                            <div className="event-detail-item">
                                <label>{selectedEvent.eventType === 'CalendarEvent' ? 'Start Date & Time' : 'Date'}</label>
                                <div className="event-detail-value">
                                    {moment(selectedEvent.start).format('YYYY-MM-DD HH:mm')}
                                </div>
                            </div>

                            {selectedEvent.eventType === 'CalendarEvent' && selectedEvent.end && (
                                <div className="event-detail-item">
                                    <label>End Date & Time</label>
                                    <div className="event-detail-value">
                                        {moment(selectedEvent.end).format('YYYY-MM-DD HH:mm')}
                                    </div>
                                </div>
                            )}

                            {selectedEvent.description && (
                                <div className="event-detail-item">
                                    <label>Description</label>
                                    <div className="event-detail-value">
                                        {selectedEvent.description}
                                    </div>
                                </div>
                            )}

                            {selectedEvent.assignedTo && (
                                <div className="event-detail-item">
                                    <label>Assigned To</label>
                                    <div className="event-detail-value">
                                        {selectedEvent.assignedTo}
                                    </div>
                                </div>
                            )}

                            {selectedEvent.taskKey && (
                                <div className="event-detail-item">
                                    <label>Task Key</label>
                                    <div className="event-detail-value">
                                        {selectedEvent.taskKey}
                                    </div>
                                </div>
                            )}

                            {selectedEvent.eventType === 'CalendarEvent' && isAdminUser && (
                                <div className="event-actions">
                                    <button
                                        type="button"
                                        className="event-delete-btn"
                                        onClick={handleDeleteCalendarEvent}
                                    >
                                        <Trash2 size={14} />
                                        Delete Event
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showScheduleModal && (
                <div className="modal-overlay" onClick={handleScheduleModalClose}>
                    <div className="event-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h5 className="modal-title">Schedule {scheduleDraft.eventTypeName || 'Event'}</h5>
                            <button
                                className="btn-close"
                                onClick={handleScheduleModalClose}
                                aria-label="Close"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="event-detail-item">
                                <label>Title</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="title"
                                    value={scheduleForm.title}
                                    onChange={handleScheduleInputChange}
                                    maxLength={500}
                                    placeholder="Enter event title"
                                />
                            </div>

                            <div className="event-detail-item">
                                <label>Start Date & Time</label>
                                <DatePicker
                                    selected={scheduleForm.startDateTime}
                                    onChange={handleStartDateTimeChange}
                                    showTimeSelect
                                    timeFormat="hh:mm aa"
                                    timeIntervals={15}
                                    dateFormat="dd/MM/yyyy h:mm aa"
                                    className="form-control"
                                    placeholderText="Select start date & time"
                                />
                            </div>

                            <div className="event-detail-item">
                                <label>End Date & Time</label>
                                <DatePicker
                                    selected={scheduleForm.endDateTime}
                                    onChange={(date) => setScheduleForm((prev) => ({ ...prev, endDateTime: date }))}
                                    showTimeSelect
                                    timeFormat="hh:mm aa"
                                    timeIntervals={15}
                                    dateFormat="dd/MM/yyyy h:mm aa"
                                    className="form-control"
                                    placeholderText="Select end date & time"
                                />
                            </div>

                            <div className="event-detail-item">
                                <label>Description</label>
                                <textarea
                                    className="form-control"
                                    name="description"
                                    rows={3}
                                    value={scheduleForm.description}
                                    onChange={handleScheduleInputChange}
                                    placeholder="Enter description"
                                />
                            </div>

                            <div className="event-actions">
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={handleScheduleModalClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={handleSaveCalendarEvent}
                                    disabled={isSavingCalendarEvent}
                                >
                                    {isSavingCalendarEvent ? 'Saving...' : 'Save Event'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CalendarMain;
